import { useState, useEffect, useCallback, useRef } from "react";
import MetricChart from '../MetricChart';
import { TradingViewResearch } from "../TradingViewResearch";
import AgentKeyModal from '../AgentKeyModal';
import { useModalAlert } from "../ModalAlert";
import { getAgentPrivateKey } from '../../utils/privateKey';
import { portfolioFetch } from '../../utils/portfolioFetch';
import {
  formatCurrency,
  formatPercent,
  getPnlColor,
  formatFloatLen,
  formatCurrencyWithDecimals,
  formatTime,
  parseUtcDate,
} from '../../utils/formatting';

const PositionInfoItem = ({ position, portfolioId, apiBaseUrl, onPositionClosed, portfolio }) => {

  const [isClosing, setIsClosing] = useState(false);
  const [agentKeyModalOpen, setAgentKeyModalOpen] = useState(false);
  const { showError, showSuccess, showWarning } = useModalAlert();

  const pnl = parseFloat(position.unrealized_pnl);
  const pnlPct = parseFloat(position.unrealized_pnl_pct/100.0);

  const closePosition = async () => {
    const isHyperliquid = portfolio?.portfolio_type === 'HYPERLIQUID';
    const agentPrivateKey = isHyperliquid ? getAgentPrivateKey(portfolioId) : null;

    if (isHyperliquid && !agentPrivateKey) {
      setAgentKeyModalOpen(true);
      return;
    }

    setIsClosing(true);
    try {
      const requestBody = {
        symbol: position.symbol,
        reason: "Manual position closure via UI",
        execute_onchain: false
      };

      const headers = {
        'accept': 'application/json',
        'Content-Type': 'application/json',
      };
      if (agentPrivateKey) {
        headers['X-Agent-Private-Key'] = agentPrivateKey;
      }

      const response = await fetch(`${apiBaseUrl}/portfolio/${portfolioId}/close-position`, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success) {
        showSuccess(
          "Position Closed",
          `Successfully closed ${position.symbol} ${position.side} position`,
          { autoClose: true }
        );

        // Show success message briefly, then remove position
        setTimeout(() => {
          onPositionClosed(position.position_id);
        }, 1000);
      } else {
        throw new Error(result.message || 'Failed to close position');
      }
    } catch (error) {
      console.error('Error closing position:', error);
      showError(
        "Position Close Failed",
        `Failed to close position: ${error.message}`,
        { autoClose: true }
      );
    } finally {
      setIsClosing(false);
    }
  };

  const handleClosePositionClick = () => {
    const pnl = parseFloat(position.unrealized_pnl);
    const pnlFormatted = formatCurrency(pnl);

    showWarning(
      'Close Position',
      `Are you sure you want to close this position?\n\nPosition Details:\n• Symbol: ${position.symbol}\n• Side: ${position.side.toUpperCase()}\n• Leverage: ${Math.round(position.leverage)}x\n• Size: ${formatCurrency(position.notional_entry)}\n• Current P&L: ${pnlFormatted}\n\nThis action cannot be undone.`,
      {
        showCancel: true,
        confirmText: 'Close Position',
        cancelText: 'Cancel',
        onConfirm: closePosition
      }
    );
  };

  return (
    <div className="m-px sm:m-2 p-1 sm:p-3 bg-slate-600/30 hover:bg-slate-500/30 transition-colors w-full sm:w-[300px] rounded border border-slate-700 flex flex-col">
      {/* Position Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 sm:gap-2 mb-1 sm:mb-2">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h4 className="text-xl font-bold text-white">{position.symbol} {Math.round(position.leverage)}x</h4>
            <span className={`px-2 py-1 rounded text-xs font-semibold ${
              position.side.toUpperCase() === 'LONG'
                ? 'bg-green-900/30 text-green-400 border border-green-800'
                : 'bg-red-900/30 text-red-400 border border-red-800'
            }`}>
              {position.side.toUpperCase()}
            </span>
          </div>
          <div className="text-sm text-slate-400">
            Opened {formatTime(position.entry_ts)}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {parseUtcDate(position.entry_ts)?.toLocaleString()}
          </div>
        </div>
        <div className="text-right">
          <div className={`text-2xl font-bold ${getPnlColor(pnl)}`}>
            {formatCurrency(pnl)}
          </div>
          <div className={`text-sm ${getPnlColor(pnlPct)}`}>
            {formatPercent(pnlPct)}
          </div>
        </div>
      </div>

      <div className="flex flex-row w-full my-2 rounded-lg">
        <TradingViewResearch
          pair={`${position.symbol}/USD`}
          portfolioType={portfolio?.portfolio_type}
          isTestnet={portfolio?.is_testnet}
        />
      </div>
      {/* Position Details Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-1 sm:gap-4 mb-2 sm:mb-4">
          <div>
            <div className="text-xs text-slate-500 mb-1">Entry Price</div>
            <div className="text-sm font-medium text-slate-300">
          ${formatFloatLen(position.entry_price, 6)}
            </div>
          </div>
          <div>
            <div className="text-xs text-slate-500 mb-1">Current Price</div>
            <div className="text-sm font-medium text-slate-300">
          ${formatFloatLen(position.current_price, 6)}
            </div>
          </div>
          <div>
            <div className="text-xs text-slate-500 mb-1">Size USD</div>
            <div className="text-sm font-medium text-slate-300">
          {formatCurrency(position.notional_entry)}
            </div>
          </div>
          <div>
            <div className="text-xs text-slate-500 mb-1">Quantity</div>
            <div className="text-sm font-medium text-slate-300">
              {formatFloatLen(position.size, 8)}
            </div>
          </div>
        </div>

        {/* Enhanced Position Details with Margin & Liquidation */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-1 sm:gap-4 mb-2 sm:mb-4">
          {position.mark_price != null && position.mark_price !== 0 && (
            <div>
              <div className="text-xs text-slate-500 mb-1">Mark Price</div>
              <div className="text-sm font-medium text-slate-300">
                ${formatFloatLen(position.mark_price, 6)}
              </div>
            </div>
          )}
          {position.liquidation_price != null && position.liquidation_price !== 0 && (
            <div>
              <div className="text-xs text-slate-500 mb-1">Liquidation Price</div>
              <div className="text-sm font-medium text-red-300">
                ${formatFloatLen(position.liquidation_price, 6)}
              </div>
            </div>
          )}
          {position.margin_used != null && position.margin_used !== 0 && (
            <div>
              <div className="text-xs text-slate-500 mb-1">Margin (Cross)</div>
              <div className="text-sm font-medium text-slate-300">
                {formatCurrency(position.margin_used)}
              </div>
            </div>
          )}
          {position.position_size_native != null && position.position_size_native !== 0 && (
            <div>
              <div className="text-xs text-slate-500 mb-1">Size ({position.symbol})</div>
              <div className="text-sm font-medium text-slate-300">
                {formatFloatLen(position.position_size_native, 8)}
              </div>
            </div>
          )}
        </div>

      {/* Exit Plan */}
      {position.exit_plan && (() => {
        // Calculate delta and determine decimal places
        const tpPrice = parseFloat(position.exit_plan.take_profit_price);
        const slPrice = parseFloat(position.exit_plan.stop_loss_price);
        const delta = Math.abs(tpPrice - slPrice);

        let decimals;
        if (delta < 1) {
          decimals = 4;
        } else if (delta < 10) {
          decimals = 3;
        } else {
          decimals = 2;
        }

        return (
          <div className="bg-slate-900/50 rounded p-1 sm:p-4 border border-slate-700/50">
            <div className="text-xs font-semibold text-slate-400 mb-1 sm:mb-3 justify-between flex flex-row w-full">
              <div>EXIT PLAN</div>
              <button
                className={`text-xs px-2 py-1 rounded transition-colors ${
                  isClosing
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'text-cyan-500 hover:bg-cyan-700/40 bg-cyan-800/40 hover:text-cyan-400 cursor-pointer'
                }`}
                onClick={handleClosePositionClick}
                disabled={isClosing}
              >
                {isClosing ? (
                  <div className="flex items-center gap-1">
                    <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                    Closing...
                  </div>
                ) : (
                  'Close Position'
                )}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-1 sm:gap-4">
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">Take Profit</span>
                <div className="text-right">
                  <div className="text-sm font-medium text-green-400">
                    {formatCurrencyWithDecimals(position.exit_plan.take_profit_price, decimals)}
                  </div>
                  <div className="text-xs text-green-400/70">
                    +{parseFloat(position.exit_plan.take_profit_pct).toFixed(1)}%
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">Stop Loss</span>
                <div className="text-right">
                  <div className="text-sm font-medium text-red-400">
                    {formatCurrencyWithDecimals(position.exit_plan.stop_loss_price, decimals)}
                  </div>
                  <div className="text-xs text-red-400/70">
                    -{parseFloat(position.exit_plan.stop_loss_pct).toFixed(1)}%
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Entry Reason */}
      {position.entry_reason && (
        <div className="mt-1 sm:mt-4 text-xs text-slate-400 italic">
          {position.entry_reason}
        </div>
      )}

      <AgentKeyModal
        isOpen={agentKeyModalOpen}
        portfolioId={portfolioId}
        isTestnet={portfolio?.is_testnet}
        portfolioName={portfolio?.name}
        onSave={() => {
          setAgentKeyModalOpen(false);
          closePosition();
        }}
        onClose={() => setAgentKeyModalOpen(false)}
      />
    </div>
  );
};

const PortfolioPerformance = ({portfolio, totalPnl, totalPnlPct, portfolioId, config, handlePositionClosed, timeWindow}) => {

  const [snapshotData, setSnapshotData] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(true);
  // Track the portfolioId we last fetched for to detect changes
  const [lastFetchedPortfolioId, setLastFetchedPortfolioId] = useState(null);

  // Clear stale snapshot data immediately when portfolioId changes
  // This prevents showing old timeseries data while new data loads
  useEffect(() => {
    if (portfolioId !== lastFetchedPortfolioId) {
      console.log(`PortfolioPerformance: portfolioId changed from ${lastFetchedPortfolioId} to ${portfolioId}, clearing stale snapshot data`);
      setSnapshotData(null);
      setSnapshotLoading(true);
      setLastFetchedPortfolioId(portfolioId);
    }
  }, [portfolioId, lastFetchedPortfolioId]);

  // Use ref to store the latest timeWindow value to avoid stale closures
  const timeWindowRef = useRef(timeWindow);
  timeWindowRef.current = timeWindow;

  const fetchPortfolioSnapshotData = useCallback(async (window) => {
    // Use passed window parameter, or fall back to current timeWindow ref
    const effectiveWindow = window || timeWindowRef.current;

    // Don't make API calls if portfolioId is null, undefined, or invalid
    if (!portfolioId || portfolioId === 'null' || portfolioId === 'undefined') {
      console.log(`PortfolioPerformance: Skipping timeseries fetch, invalid portfolioId:`, portfolioId);
      setSnapshotLoading(false);
      return;
    }

    console.log('SNAPSHOT: fetchPortfolioSnapshotData called for portfolioId:', portfolioId, 'window:', effectiveWindow, 'at', new Date().toLocaleTimeString());
    setSnapshotLoading(true);

    try {
      // Build URL with agent parameters if available
      // Access portfolio from the current state instead of closure
      const currentPortfolio = portfolio;

      // Get agent private key from localStorage using correct format
      let agentPrivateKey = null;
      if (currentPortfolio?.id) {
        const testnetKey = `hyperliquid_agent_testnet_${currentPortfolio.id}`;
        const mainnetKey = `hyperliquid_agent_mainnet_${currentPortfolio.id}`;
        agentPrivateKey = localStorage.getItem(testnetKey) || localStorage.getItem(mainnetKey);
        // Also try legacy format for backward compatibility
        if (!agentPrivateKey) {
          agentPrivateKey = localStorage.getItem(`agent_private_key_${currentPortfolio.id}`);
        }
      }

      const apiBaseUrl = config.apiBaseUrl || 'http://0.0.0.0:8000';
      const url = new URL(`${apiBaseUrl}/portfolio/${portfolioId}/timeseries`);

      // Select granularity based on window size for optimal data resolution
      let granularity;
      switch (effectiveWindow) {
        case '24H':
          granularity = '15m'; // More detailed for short term
          break;
        case '7D':
          granularity = '4h'; // Balanced for medium term
          break;
        case '30D':
          granularity = '24h'; // Efficient for long term
          break;
        default:
          granularity = '1h'; // Default granularity
      }

      url.searchParams.append('granularity', granularity);
      url.searchParams.append('limit', '100');
      url.searchParams.append('window', effectiveWindow); // Add window parameter

      // Add user_master_address if available (non-sensitive)
      if (currentPortfolio?.user_address) {
        url.searchParams.append('user_master_address', currentPortfolio.user_address);
      }

      // SECURITY: Use X-Agent-Private-Key header instead of query param (not visible in logs/URLs)
      const headers = {
        'accept': 'application/json'
      };

      // Use portfolioFetch to automatically add X-Agent-Private-Key header
      const response = await portfolioFetch(url.toString(), {
        method: 'GET',
        headers
      }, portfolioId);

      if (!response.ok) {
        console.error('Failed to fetch portfolio snapshot data');
        return;
      }

      const data = await response.json();
      console.log('Fetched portfolio snapshot data:', data);
      setSnapshotData(data.timeseries);
    } catch (error) {
      console.error('Error fetching snapshot data:', error);
    } finally {
      setSnapshotLoading(false);
    }
  }, [portfolioId, config.apiBaseUrl, portfolio]);

  // Debounce and synchronize metrics fetch
  const lastFetchParamsRef = useRef({ portfolioId: null, timeWindow: null });
  const debounceTimeoutRef = useRef(null);

  useEffect(() => {
    // Only fetch if portfolioId or timeWindow actually changed
    if (
      portfolioId !== lastFetchParamsRef.current.portfolioId ||
      timeWindow !== lastFetchParamsRef.current.timeWindow
    ) {
      // Debounce: clear any pending fetch
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(() => {
        lastFetchParamsRef.current = { portfolioId, timeWindow };
        fetchPortfolioSnapshotData(timeWindow);
      }, 200); // 200ms debounce
    }
    // Cleanup on unmount
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [portfolioId, timeWindow, fetchPortfolioSnapshotData]);

  return (
      <div className="w-full flex flex-col">
        <div>
          {snapshotLoading ? (
            <div className="flex items-center justify-center h-24 sm:h-32 bg-slate-800/50 border border-slate-700 rounded">
              <svg className="animate-spin h-8 w-8 text-cyan-400 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-slate-400">Loading chart data...</span>
            </div>
          ) : (
            <MetricChart
              data={snapshotData || []}
              value_field="equity"
              timeWindow={timeWindow}
              currentValue={portfolio?.total_value_usd ?? null}
              initialValue={portfolio?.cash_balance_usd ?? portfolio?.total_value_usd ?? null}
            />
          )}
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-0.5 sm:gap-2 mt-0.5 sm:mt-2 mb-0.5 sm:mb-2">

          {/* Total Value */}
          <div className="bg-slate-800 rounded p-1 sm:p-4 border border-slate-700">
            <div className="text-slate-400 text-xs sm:text-sm mb-0.5 sm:mb-1">Total</div>
            <div className="text-base sm:text-2xl font-bold text-white">
              {formatCurrency(portfolio.total_value_usd)}
            </div>
          </div>

          {/* Cash Balance */}
          <div className="bg-slate-800 rounded p-1 sm:p-4 border border-slate-700">
            <div className="text-slate-400 text-xs sm:text-sm mb-0.5 sm:mb-1">Cash Balance</div>
            <div className="text-base sm:text-2xl font-bold text-white">
              {formatCurrency(portfolio.cash_balance_usd)}
            </div>
          </div>

          {/* Available Balance */}
          <div className="bg-slate-800 rounded p-1 sm:p-4 border border-slate-700">
            <div className="text-slate-400 text-xs sm:text-sm mb-0.5 sm:mb-1">Avail Bal</div>
            <div className="text-base sm:text-2xl font-bold text-white">
              {formatCurrency(portfolio.available_balance_usd)}
            </div>
          </div>

          {/* Total P&L — matches the Hyperliquid platform's 24h PnL widget
              (account-value delta: realized + unrealized + funding). */}
          <div className="bg-slate-800 rounded p-1 sm:p-4 border border-slate-700">
            <div className="text-slate-400 text-xs sm:text-sm mb-0.5 sm:mb-1">P&L</div>
            <div className={`text-base sm:text-2xl font-bold ${getPnlColor(portfolio.total_pnl_usd)}`}>
              {formatCurrency(portfolio.total_pnl_usd)}
            </div>
            <div className={`text-xs sm:text-sm ${getPnlColor(portfolio.total_pnl_pct)}`}>
              {formatPercent(portfolio.total_pnl_pct)}
            </div>
          </div>

          {/* Trade Fees */}
          <div className="bg-slate-800 rounded p-1 sm:p-4 border border-slate-700">
            <div className="text-slate-400 text-xs sm:text-sm mb-0.5 sm:mb-1">Fees</div>
            <div className="text-base sm:text-2xl font-bold text-white">
              {formatCurrency(portfolio.trade_fees_usd)}
            </div>
          </div>

          {/* Positions */}
          <div className="bg-slate-800 rounded p-1 sm:p-4 border border-slate-700">
            <div className="text-slate-400 text-xs sm:text-sm mb-0.5 sm:mb-1">Pos</div>
            <div className="text-base sm:text-2xl font-bold text-white">
              {portfolio.open_positions.length}/{portfolio.max_positions}
            </div>
            <div className="text-xs sm:text-sm text-slate-400">
              {portfolio.closed_positions.length} closed
            </div>
          </div>
        </div>

        {/* Open Positions */}
        {portfolio.open_positions.length > 0 && (
          <div className="bg-slate-800 rounded border border-slate-700 overflow-hidden">
            <div className="px-1 sm:px-4 py-1 sm:py-2 border-b border-slate-700 flex flex-row items-center">
              <h3 className="text-sm sm:text-lg font-semibold text-white mt-0 mb-0">Open Positions</h3>
              <span className="ml-3 text-slate-500 text-sm italic">Positions may take time to refresh...</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:flex-wrap">
              {portfolio.open_positions.map((position, index) => {
                return (
                  <PositionInfoItem
                    key={`position-${position.position_id || position.symbol || index}`}
                    position={position}
                    portfolioId={portfolioId}
                    apiBaseUrl={config.apiBaseUrl}
                    onPositionClosed={handlePositionClosed}
                    portfolio={portfolio}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* No Positions Message */}
        {portfolio.open_positions.length === 0 && (
          <div className="bg-slate-800 rounded border border-slate-700 p-2 sm:p-12 text-center">
            <svg className="w-16 h-16 text-slate-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-lg font-semibold text-slate-400 mb-2">No Open Positions</h3>
            <p className="text-sm text-slate-500">
              Your portfolio is currently holding only cash. Start trading to see positions here.
            </p>
          </div>
        )}

        {/* Risk Metrics — only show when there is at least one meaningful value */}
        {(parseFloat(portfolio.sharpe_ratio) !== 0 || parseFloat(portfolio.win_rate) !== 0 || parseFloat(portfolio.max_drawdown_pct) !== 0) && (
          <div className="bg-slate-800 rounded border border-slate-700 p-1 sm:p-6">
            <h3 className="text-sm sm:text-lg font-semibold text-white mb-1 sm:mb-4">Risk</h3>
            <div className="grid grid-cols-3 gap-1 sm:gap-4">
              {parseFloat(portfolio.sharpe_ratio) !== 0 && (
                <div>
                  <div className="text-xs text-slate-500 mb-1">Sharpe Ratio</div>
                  <div className="text-lg font-semibold text-white">
                    {parseFloat(portfolio.sharpe_ratio).toFixed(2)}
                  </div>
                </div>
              )}
              {parseFloat(portfolio.win_rate) !== 0 && (
                <div>
                  <div className="text-xs text-slate-500 mb-1">Win Rate</div>
                  <div className="text-lg font-semibold text-white">
                    {formatPercent(portfolio.win_rate)}
                  </div>
                </div>
              )}
              {parseFloat(portfolio.max_drawdown_pct) !== 0 && (
                <div>
                  <div className="text-xs text-slate-500 mb-1">Max Drawdown</div>
                  <div className="text-lg font-semibold text-red-400">
                    {formatPercent(portfolio.max_drawdown_pct)}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

      </div>

  );
};

export default PortfolioPerformance;
